import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, ExternalLink, Upload } from "lucide-react";
import api, { formatApiError, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const emptyForm = { name: "", location: "Dahod, Gujarat", status: "ongoing", config: "", image: "", description: "", features: "", gallery: [], specs: "", floor_plans: [] };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGalleryUpload = async (e, field = "gallery") => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        setForm((f) => ({ ...f, [field]: [...(f[field] || []), data.url] }));
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const load = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, features: (p.features || []).join(", "), gallery: p.gallery || [], specs: (p.specs || []).join("\n"), floor_plans: p.floor_plans || [] });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      location: form.location,
      status: form.status,
      config: form.config,
      image: form.image,
      description: form.description,
      features: form.features.split(",").map((f) => f.trim()).filter(Boolean),
      gallery: form.gallery || [],
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
      floor_plans: form.floor_plans || [],
    };
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markRead = async (enq) => {
    try {
      await api.patch(`/enquiries/${enq.id}/read`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const inputCls =
    "w-full bg-black border border-white/15 focus:border-[#C5A059] outline-none px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo.webp" alt="" className="h-9 w-9 object-cover rounded-full border border-[#C5A059]/40" />
            <span className="font-display tracking-wide">TAHA ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-zinc-400 hover:text-[#C5A059] transition-colors" data-testid="admin-view-site-link" title="View site">
              <ExternalLink size={18} />
            </a>
            <span className="text-sm text-zinc-500 hidden sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-400 transition-colors" data-testid="admin-logout-button">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <Tabs defaultValue="projects">
          <TabsList className="bg-[#0A0A0A] border border-white/10 mb-8">
            <TabsTrigger value="projects" data-testid="tab-projects">Projects ({projects.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              Enquiries ({enquiries.filter((e) => !e.read).length} new)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl" data-testid="projects-manager-title">Manage Projects</h2>
              <button onClick={openCreate} className="flex items-center gap-2 bg-[#C5A059] text-black font-accent text-xs tracking-[0.2em] uppercase px-5 py-3 hover:bg-[#D4AF37] transition-colors" data-testid="add-project-button">
                <Plus size={14} /> Add Project
              </button>
            </div>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="projects-list">
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-5 p-4 hover:bg-white/[0.03] transition-colors" data-testid={`admin-project-row-${p.id}`}>
                  <img src={imgSrc(p.image)} alt="" className="w-20 h-14 object-cover border border-white/10 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{p.config} — {p.location}</p>
                  </div>
                  <Badge variant="outline" className={p.status === "ongoing" ? "border-[#C5A059]/50 text-[#C5A059]" : "border-zinc-600 text-zinc-400"}>
                    {p.status}
                  </Badge>
                  <button onClick={() => openEdit(p)} className="p-2 text-zinc-400 hover:text-[#C5A059] transition-colors" data-testid={`edit-project-${p.id}`}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(p)} className="p-2 text-zinc-400 hover:text-red-400 transition-colors" data-testid={`delete-project-${p.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {projects.length === 0 && <p className="p-10 text-zinc-500 text-sm">No projects yet. Add your first one.</p>}
            </div>
          </TabsContent>

          <TabsContent value="enquiries">
            <h2 className="font-display text-2xl mb-6" data-testid="enquiries-title">Customer Enquiries</h2>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="enquiries-list">
              {enquiries.map((enq) => (
                <div key={enq.id} className={`p-5 ${enq.read ? "opacity-60" : ""}`} data-testid={`enquiry-row-${enq.id}`}>
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <p className="font-medium">{enq.name} <span className="text-zinc-500 text-sm">· {enq.phone}{enq.email ? ` · ${enq.email}` : ""}</span></p>
                      {enq.project && <p className="text-xs text-[#C5A059] mt-1">Interested in: {enq.project}</p>}
                      {enq.visit_date && (
                        <p className="text-xs text-zinc-400 mt-1" data-testid={`enquiry-visit-${enq.id}`}>
                          Site visit: {enq.visit_date}{enq.visit_time ? ` at ${enq.visit_time}` : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-600">{new Date(enq.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {!enq.read && (
                        <button onClick={() => markRead(enq)} className="text-xs font-accent tracking-widest uppercase border border-[#C5A059]/40 text-[#C5A059] px-3 py-1.5 hover:bg-[#C5A059] hover:text-black transition-colors" data-testid={`mark-read-${enq.id}`}>
                          Mark Read
                        </button>
                      )}
                    </div>
                  </div>
                  {enq.message && <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{enq.message}</p>}
                </div>
              ))}
              {enquiries.length === 0 && <p className="p-10 text-zinc-500 text-sm">No enquiries yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-[#C5A059]/25 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Project" : "Add New Project"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="project-form">
            <input required placeholder="Project Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="project-name-input" />
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} data-testid="project-location-input" />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-black border-white/15 text-white" data-testid="project-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 text-white">
                  <SelectItem value="ongoing" data-testid="status-ongoing">Ongoing</SelectItem>
                  <SelectItem value="completed" data-testid="status-completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input placeholder="Configuration (e.g. 2 & 3 BHK Residences)" value={form.config} onChange={(e) => setForm({ ...form, config: e.target.value })} className={inputCls} data-testid="project-config-input" />
            <div className="space-y-2">
              <div className="flex gap-2">
                <input placeholder="Image URL or path (e.g. /images/project.jpg)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} data-testid="project-image-input" />
                <label className="shrink-0 cursor-pointer border border-[#C5A059]/50 text-[#C5A059] px-4 flex items-center gap-2 font-accent text-xs tracking-widest uppercase hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="project-image-upload-label">
                  {uploading ? "Uploading..." : <><Upload size={14} /> Upload</>}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="project-image-file-input" />
                </label>
              </div>
              {form.image && <img src={imgSrc(form.image)} alt="Preview" className="h-20 object-cover border border-white/10" data-testid="project-image-preview" />}
            </div>
            <div className="space-y-2">
              <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                Gallery Photos ({(form.gallery || []).length}) — customers can swipe through these
              </p>
              <div className="flex flex-wrap gap-2" data-testid="gallery-manager">
                {(form.gallery || []).map((g, i) => (
                  <div key={i} className="relative">
                    <img src={imgSrc(g)} alt={`Gallery ${i + 1}`} className="h-16 w-20 object-cover border border-white/10" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }))}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-black border border-red-400/60 text-red-400 text-xs rounded-full flex items-center justify-center hover:bg-red-400 hover:text-black transition-colors"
                      data-testid={`gallery-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="h-16 w-20 border border-dashed border-[#C5A059]/50 text-[#C5A059] flex items-center justify-center cursor-pointer hover:bg-[#C5A059]/10 transition-colors" data-testid="gallery-upload-label">
                  {uploading ? "..." : <Plus size={16} />}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={handleGalleryUpload} disabled={uploading} data-testid="gallery-file-input" />
                </label>
              </div>
            </div>
            <textarea rows={3} placeholder="Specifications, one per line (e.g. 2 BHK — 1250 sq.ft)" value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} className={`${inputCls} resize-none`} data-testid="project-specs-input" />
            <div className="space-y-2">
              <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                Floor Plans ({(form.floor_plans || []).length}) — shown on the project detail page
              </p>
              <div className="flex flex-wrap gap-2" data-testid="floorplan-manager">
                {(form.floor_plans || []).map((fp, i) => (
                  <div key={i} className="relative">
                    <img src={imgSrc(fp)} alt={`Floor plan ${i + 1}`} className="h-16 w-20 object-cover border border-white/10" />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, floor_plans: f.floor_plans.filter((_, x) => x !== i) }))}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-black border border-red-400/60 text-red-400 text-xs rounded-full flex items-center justify-center hover:bg-red-400 hover:text-black transition-colors"
                      data-testid={`floorplan-remove-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="h-16 w-20 border border-dashed border-[#C5A059]/50 text-[#C5A059] flex items-center justify-center cursor-pointer hover:bg-[#C5A059]/10 transition-colors" data-testid="floorplan-upload-label">
                  {uploading ? "..." : <Plus size={16} />}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={(e) => handleGalleryUpload(e, "floor_plans")} disabled={uploading} data-testid="floorplan-file-input" />
                </label>
              </div>
            </div>
            <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} data-testid="project-description-input" />
            <input placeholder="Features, comma separated (e.g. Lift, Parking, Balcony)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className={inputCls} data-testid="project-features-input" />
            <button type="submit" disabled={saving} className="w-full bg-[#C5A059] text-black font-accent text-xs tracking-[0.25em] uppercase px-8 py-3.5 hover:bg-[#D4AF37] transition-colors disabled:opacity-50" data-testid="project-save-button">
              {saving ? "Saving..." : editing ? "Update Project" : "Add Project"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
